import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import { articles } from "@/mocks/articles";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center bg-background-50">
          <div className="text-center px-4">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center text-foreground-300">
              <i className="ri-file-unknow-line text-4xl"></i>
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">
              文章未找到
            </h1>
            <p className="text-foreground-600 mb-6">
              抱歉，您查找的文章不存在或已被移除。
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-2.5 text-sm bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer font-medium"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            image: article.imageUrl,
            datePublished: article.date,
            author: {
              "@type": "Organization",
              name: article.author,
            },
            publisher: {
              "@type": "Organization",
              name: "AI美女生成平台",
            },
            url: `https://ai-beauty-platform.com/article/${article.slug}`,
            isPartOf: {
              "@type": "WebSite",
              name: "AI美女生成平台",
              url: "https://ai-beauty-platform.com",
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "首页",
                  item: "https://ai-beauty-platform.com/",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "教程资讯",
                  item: "https://ai-beauty-platform.com/article/ai-meinv-shengcheng-rumen",
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: article.title,
                },
              ],
            },
          }),
        }}
      />
      <Navbar />
      <main className="min-h-screen pt-16 bg-background-50">
        {/* Breadcrumb */}
        <div className="border-b border-background-200/70">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
              <Link
                to="/"
                className="hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                首页
              </Link>
              <span>/</span>
              <Link
                to="/article/ai-meinv-shengcheng-rumen"
                className="hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                教程资讯
              </Link>
              <span>/</span>
              <span className="text-foreground-950 font-medium truncate max-w-[200px]">
                {article.title}
              </span>
            </nav>
          </div>
        </div>

        {/* Article Header */}
        <header className="py-10 md:py-14">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 flex-wrap mb-4">
              <span className="text-xs px-3 py-1 rounded-full bg-primary-100 text-primary-700">
                {article.category}
              </span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-base text-foreground-600 leading-relaxed mb-6">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <span>{article.author}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>{article.date}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>阅读时间 {article.readTime}</span>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        <div className="max-w-4xl mx-auto px-4 md:px-6 mb-10">
          <div className="rounded-2xl overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full aspect-[16/10] object-cover object-center"
            />
          </div>
        </div>

        {/* Article Content */}
        <article className="pb-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div
              className="prose prose-base max-w-none
                prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-semibold
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-5
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5
                prose-strong:text-foreground-950 prose-strong:font-semibold
                prose-li:text-foreground-700"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        {/* Related Articles */}
        <section className="py-16 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl font-heading font-semibold text-foreground-950 mb-8 text-center">
              相关文章推荐
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {articles
                .filter((a) => a.slug !== article.slug)
                .slice(0, 4)
                .map((a) => (
                  <Link
                    key={a.slug}
                    to={`/article/${a.slug}`}
                    className="group flex gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden">
                      <img
                        src={a.imageUrl}
                        alt={a.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-foreground-950 mb-1.5 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {a.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 mb-2">
                        {a.excerpt}
                      </p>
                      <span className="text-xs text-foreground-400">
                        {a.date}
                      </span>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}